import { MaskService } from '@shared/services/mask.service';
import { CustomDateAdapter } from '@shared/services/custom-date-adapter';
import { DateAdapter, MAT_DATE_LOCALE } from '@angular/material/core';
import { NgxMaskDirective, provideNgxMask } from 'ngx-mask';
import { Component, OnInit, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule, CurrencyPipe, DatePipe } from '@angular/common';
import { FormBuilder, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { normalizarValorOrdenacao } from '../../../shared/helpers/ordenacao-tabela.helper';
import { MatSort, MatSortHeader, MatSortModule } from '@angular/material/sort';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSelectModule } from '@angular/material/select';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatMenuModule, MatMenuTrigger } from '@angular/material/menu';
import { MatBadgeModule } from '@angular/material/badge';

import { EmptyListComponent } from '../../../shared/components/empty-list/empty-list.component';
import { LoadingBoxComponent } from '../../../shared/components/loading-box/loading-box.component';
import { HeaderComponent } from '../../../shared/components/header/header.component';

import { AlertService, AlertType } from '../../../shared/services/alert.service';
import { UtilService } from '../../../shared/services/util.service';
import { NFCeContingenciaService } from '../../../shared/services/apis/dfe/nfce-contingencia.service';
import { EmissorService } from '../../../shared/services/apis/dfe/emissor.service';
import { ListaNFCeContingenciaRequest } from '../../../shared/services/requests/dfe/lista-nfce-contingencia.request';
import { ListaNFCeContingenciaResponse } from '../../../shared/services/responses/dfe/lista-nfce-contingencia.response';
import { NFCeContingenciaStruct } from '../../../shared/services/structs/dfe/nfce-contingencia.struct';
import { ListaEmissorStruct } from '../../../shared/services/structs/dfe/lista-emissor.struct';
import { ModuloEnum } from '../../../shared/enums/usuario/modulo.enum';
import { FuncionalidadeEnum } from '../../../shared/enums/usuario/funcionalidade.enum';
import { NFCeContingenciaRefinamentoEnum } from '../../../shared/enums/dfe/nfce-contingencia-refinamento.enum';

export enum PeriodoEnum {
  SemFiltro = 0,
  Ultimos30Dias = 1,
  DoMes = 2,
  DoIntervalo = 3,
}

@Component({
  selector: 'app-nfces-em-contingencia',
  standalone: true,
  imports: [
    NgxMaskDirective,
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    MatTableModule,
    MatSortModule,
    MatSort,
    MatSortHeader,
    MatIconModule,
    MatButtonModule,
    MatFormFieldModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MatSelectModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatMenuModule,
    MatBadgeModule,
    CurrencyPipe,
    DatePipe,
    EmptyListComponent,
    LoadingBoxComponent,
    HeaderComponent,
  ],
  providers: [
    provideNgxMask(),
    { provide: MAT_DATE_LOCALE, useValue: 'pt-BR' },
    { provide: DateAdapter, useClass: CustomDateAdapter },
  ],
  templateUrl: './nfces-em-contingencia.component.html',
  styleUrls: ['./nfces-em-contingencia.component.css'],
})
export class NFCesEmContingenciaComponent implements OnInit, OnDestroy {
  public readonly onDateInput = MaskService.onDateInput;
  public moduloEnum = ModuloEnum.Home;
  public funcionalidadeEnum = FuncionalidadeEnum.NotasFiscais_NFCesEmContingencia;

  // FormGroup
  public model!: FormGroup;

  // Refinamento de busca
  public filtroRefinar: NFCeContingenciaRefinamentoEnum | null = null;
  public _refinamentoEnum = NFCeContingenciaRefinamentoEnum;

  // Seletor de periodo
  public periodoSelecionado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  public PeriodoEnum = PeriodoEnum;
  public periodoIntervaloDe: Date | null = null;
  public periodoIntervaloAte: Date | null = null;

  // Estado
  public _isLoading = false;
  public _ultimaPagina = false;
  public _paginaAtual = 0;

  // Dados
  public _listaResponse: ListaNFCeContingenciaResponse | undefined;
  public _dataSource = new MatTableDataSource<NFCeContingenciaStruct>();
  public displayedColumns: string[] = [
    'nfce',
    'serie',
    'emissor',
    'tipoAmbiente',
    'data',
    'cliente',
    'valor',
    'situacao',
    'retornoSefaz',
  ];
  // Último estado de período aplicado
  private periodoSelecionadoAplicado: PeriodoEnum = PeriodoEnum.Ultimos30Dias;
  private periodoIntervaloDeAplicado: Date | null = null;
  private periodoIntervaloAteAplicado: Date | null = null;
  private periodoMenuFoiAplicado = false;

  private cloneDate(data: Date | null): Date | null {
    return data ? new Date(data) : null;
  }

  private salvarPeriodoAplicado(): void {
    this.periodoSelecionadoAplicado = this.periodoSelecionado;
    this.periodoIntervaloDeAplicado = this.cloneDate(this.periodoIntervaloDe);
    this.periodoIntervaloAteAplicado = this.cloneDate(this.periodoIntervaloAte);
  }

  private restaurarPeriodoAplicado(): void {
    this.periodoSelecionado = this.periodoSelecionadoAplicado;
    this.periodoIntervaloDe = this.cloneDate(this.periodoIntervaloDeAplicado);
    this.periodoIntervaloAte = this.cloneDate(this.periodoIntervaloAteAplicado);
  }

  // Ao abrir o menu de período, prepara estado de interação
  prepararPeriodoIntervalo(): void {
    this.periodoMenuFoiAplicado = false;
  }

  // Cancela intervalo: restaura estado aplicado e fecha o menu
  cancelarPeriodoIntervalo(trigger?: MatMenuTrigger): void {
    this.restaurarPeriodoAplicado();
    this.periodoMenuFoiAplicado = true;
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  onPeriodoMenuClosed(): void {
    if (!this.periodoMenuFoiAplicado) {
      this.restaurarPeriodoAplicado();
    }
  }

  @ViewChild('periodoMenuTrigger') periodoMenuTrigger?: MatMenuTrigger;

  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this._dataSource.sort = sort;
    this._dataSource.sortingDataAccessor = (item, property) => {
      switch (property) {
        case 'tipoAmbiente': return normalizarValorOrdenacao(item.tipoAmbiente);
        case 'nfce': return normalizarValorOrdenacao(item.numero);
        case 'serie': return normalizarValorOrdenacao(item.serie);
        case 'emissor': return normalizarValorOrdenacao(item.nomeEmissor || '');
        case 'data': return normalizarValorOrdenacao(item.dataEmissao || '');
        case 'cliente': return normalizarValorOrdenacao(this.getClienteDisplay(item));
        case 'valor': return normalizarValorOrdenacao(item.valorTotal);
        case 'situacao': return normalizarValorOrdenacao(item.statusSecundarioDocumento || 0);
        default: return normalizarValorOrdenacao((item as any)[property]);
      }
    };
  }

  // Filtros avancados (do dialog)
  public filtroIdTipoAmbiente: number | null = null;
  public filtroIdStatusSecundarioDocumento: number | null = null;
  public filtroIdEmissor: number | null = null;
  public filtroSerie: number | null = null;

  public filtroIdTipoAmbienteDraft: number | null = null;
  public filtroIdStatusSecundarioDocumentoDraft: number | null = null;
  public filtroIdEmissorDraft: number | null = null;
  public filtroSerieDraft: number | null = null;

  // Lookup
  public emissores: ListaEmissorStruct[] = [];
  public loadLookups = false;

  constructor(
    private formBuilder: FormBuilder,
    private nfceContingenciaService: NFCeContingenciaService,
    private emissorService: EmissorService,
    private alertService: AlertService,
    private utilService: UtilService
  ) { }

  ngOnInit(): void {
    this.model = this.formBuilder.group({
      pesquisa: [''],
    });

    // Periodo padrao: ultimos 30 dias
    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.carregarLookups();
  }

  ngOnDestroy(): void {
    this.utilService.salvarFiltro(this.moduloEnum, this.funcionalidadeEnum, this.model, [
      '/notas-fiscais/nfces-em-contingencia',
    ]);
  }

  // --- LOOKUPS ---

  private carregarLookups(): void {
    this.loadLookups = true;
    this.emissorService.getAll('', 0).subscribe({
      next: (response) => {
        if (!response.isError) {
          this.emissores = response.listaEmissorStruct || [];
        }
        this.loadLookups = false;
        this.loadFilters();
        this.pesquisar();
      },
      error: () => {
        this.loadLookups = false;
      },
    });
  }

  private loadFilters(): void {
    const filtroSalvo = this.utilService.obterFiltro(this.moduloEnum, this.funcionalidadeEnum);
    if (filtroSalvo) {
      this.model.patchValue(filtroSalvo);
    }
  }

  // --- SELETOR DE PERIODO ---

  get periodoLabel(): string {
    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return 'sem filtro';
      case PeriodoEnum.Ultimos30Dias:
        return '\u00FAltimos 30 dias';
      case PeriodoEnum.DoMes:
        return 'do m\u00EAs';
      case PeriodoEnum.DoIntervalo:
        return 'do intervalo';
      default:
        return '\u00FAltimos 30 dias';
    }
  }

  selecionarPeriodo(periodo: PeriodoEnum): void {
    this.periodoSelecionado = periodo;
  }

  private setPeriodoUltimos30Dias(): void {
    this.periodoSelecionado = PeriodoEnum.Ultimos30Dias;
    this.periodoIntervaloDe = null;
    this.periodoIntervaloAte = null;
  }

  private getDatasPeriodo(): { inicio: Date | null; fim: Date | null } {
    const hoje = new Date();
    switch (this.periodoSelecionado) {
      case PeriodoEnum.SemFiltro:
        return { inicio: null, fim: null };
      case PeriodoEnum.Ultimos30Dias: {
        const trintaDiasAtras = new Date();
        trintaDiasAtras.setDate(hoje.getDate() - 30);
        return { inicio: trintaDiasAtras, fim: hoje };
      }
      case PeriodoEnum.DoMes: {
        const inicioMes = new Date(hoje.getFullYear(), hoje.getMonth(), 1);
        const fimMes = new Date(hoje.getFullYear(), hoje.getMonth() + 1, 0);
        return { inicio: inicioMes, fim: fimMes };
      }
      case PeriodoEnum.DoIntervalo:
        return { inicio: this.periodoIntervaloDe, fim: this.periodoIntervaloAte };
      default:
        return { inicio: null, fim: null };
    }
  }

  aplicarPeriodo(trigger?: MatMenuTrigger): void {
    this.periodoMenuFoiAplicado = true;
    this.salvarPeriodoAplicado();
    this.clickPesquisar();
    const menuTrigger = trigger ?? this.periodoMenuTrigger;
    setTimeout(() => menuTrigger?.closeMenu());
  }

  // --- FILTROS AVANCADOS (Menu) ---

  prepararFiltrosAvancados(): void {
    this.filtroIdTipoAmbienteDraft = this.filtroIdTipoAmbiente;
    this.filtroIdStatusSecundarioDocumentoDraft = this.filtroIdStatusSecundarioDocumento;
    this.filtroIdEmissorDraft = this.filtroIdEmissor;
    this.filtroSerieDraft = this.filtroSerie;
  }

  aplicarFiltrosAvancados(): void {
    this.filtroIdTipoAmbiente = this.filtroIdTipoAmbienteDraft;
    this.filtroIdStatusSecundarioDocumento = this.filtroIdStatusSecundarioDocumentoDraft;
    this.filtroIdEmissor = this.filtroIdEmissorDraft;
    this.filtroSerie = this.filtroSerieDraft;
    this.clickPesquisar();
  }

  cancelarFiltrosAvancados(): void {
    this.prepararFiltrosAvancados();
  }

  get qtdFiltrosAvancados(): number {
    let count = 0;
    if (this.filtroIdTipoAmbiente != null) count++;
    if (this.filtroIdStatusSecundarioDocumento != null) count++;
    if (this.filtroIdEmissor != null) count++;
    if (this.filtroSerie != null) count++;
    return count;
  }

  get pesquisaPlaceholderLabel(): string {
    return this.filtroRefinar === NFCeContingenciaRefinamentoEnum.cliente
      ? 'Buscar por cliente'
      : 'Buscar pelo número';
  }

  get pesquisaPlaceholderInput(): string {
    return this.filtroRefinar === NFCeContingenciaRefinamentoEnum.cliente
      ? 'nome do cliente...'
      : 'número da nota fiscal...';
  }

  get isPesquisaPorNumero(): boolean {
    return this.filtroRefinar !== NFCeContingenciaRefinamentoEnum.cliente;
  }

  selecionarRefinamento(refinamento: NFCeContingenciaRefinamentoEnum | null): void {
    this.filtroRefinar = refinamento;
    const pesquisaAtual = this.model.get('pesquisa')?.value;
    this.model.get('pesquisa')?.setValue(this.normalizarPesquisa(pesquisaAtual), { emitEvent: false });
  }

  onPesquisaInput(valor: unknown): void {
    const valorNormalizado = this.normalizarPesquisa(valor);
    if (valorNormalizado !== this.model.get('pesquisa')?.value) {
      this.model.get('pesquisa')?.setValue(valorNormalizado, { emitEvent: false });
    }
  }

  private normalizarPesquisa(valor: unknown): string {
    const texto = String(valor ?? '');
    if (this.isPesquisaPorNumero) {
      return texto.replace(/\D/g, '').slice(0, 8);
    }

    return texto.slice(0, 100);
  }

  normalizarSerie(valor: unknown): number | null {
    const serieSomenteNumeros = String(valor ?? '').replace(/\D/g, '').slice(0, 3);
    return serieSomenteNumeros ? Number(serieSomenteNumeros) : null;
  }

  // --- PESQUISA ---

  pesquisar(): void {
    if (this._isLoading) return;

    this._isLoading = true;
    const request = this.getFiltrosRequest();

    this.nfceContingenciaService.getAll(request, this._paginaAtual, 20).subscribe({
      next: (response: ListaNFCeContingenciaResponse) => {
        this._isLoading = false;

        if (response.isError) {
          this.alertService.show('Erro', response.errorDescription, AlertType.error);
          return;
        }

        if (!response.nfces || response.nfces.length === 0) {
          this._ultimaPagina = true;
        }

        if (!this._listaResponse) {
          this._listaResponse = response;
          this._dataSource.data = response.nfces || [];
        } else {
          this._listaResponse.nfces.push(...(response.nfces || []));
          this._dataSource.data = this._listaResponse.nfces;
        }

        if (response.nfces.length === 0 || !response.possuiProximaPagina) {
          this._ultimaPagina = true;
        }
      },
      error: () => {
        this._isLoading = false;
        this.alertService.show('Erro', 'Falha ao carregar notas em conting\u00EAncia.', AlertType.error);
      },
    });
  }

  public clickPesquisar(): void {
    this._paginaAtual = 0;
    this._listaResponse = undefined;
    this._dataSource.data = [];
    this._ultimaPagina = false;
    this.pesquisar();
  }

  public proximaPagina(): void {
    this._paginaAtual++;
    this.pesquisar();
  }

  public limparFiltros(): void {
    this.model.patchValue({ pesquisa: '' });
    this.filtroRefinar = null;
    this.filtroIdTipoAmbiente = null;
    this.filtroIdStatusSecundarioDocumento = null;
    this.filtroIdEmissor = null;
    this.filtroSerie = null;
    this.setPeriodoUltimos30Dias();
    this.salvarPeriodoAplicado();
    this.clickPesquisar();
  }

  // --- FILTROS REQUEST ---

  public getFiltrosRequest(): ListaNFCeContingenciaRequest {
    const request = new ListaNFCeContingenciaRequest();
    const datas = this.getDatasPeriodo();

    // Texto de pesquisa rápida
    const pesquisaTexto = this.model.get('pesquisa')?.value?.trim();
    if (pesquisaTexto) {
      if (this.filtroRefinar === NFCeContingenciaRefinamentoEnum.cliente) {
        request.pesquisarCliente = pesquisaTexto;
      } else {
        // Default: buscar por número
        request.pesquisarNumero = pesquisaTexto;
      }
    }

    request.idEmissor = this.filtroIdEmissor;
    request.idTipoAmbiente = this.filtroIdTipoAmbiente;
    request.idStatusSecundarioDocumento = this.filtroIdStatusSecundarioDocumento;
    request.serie = this.filtroSerie;

    // Período definido exclusivamente pelo seletor de intervalo.
    // data_emissao é armazenada em UTC: normalizamos para início e fim do dia local
    // para que a data final cubra o dia inteiro após a conversão de fuso (toISOString).
    request.dataHoraEmissaoInicial = this.inicioDoDia(datas.inicio);
    request.dataHoraEmissaoFinal = this.fimDoDia(datas.fim);

    return request;
  }

  private inicioDoDia(data: Date | null): Date | null {
    if (!data) return null;
    const inicio = new Date(data);
    inicio.setHours(0, 0, 0, 0);
    return inicio;
  }

  private fimDoDia(data: Date | null): Date | null {
    if (!data) return null;
    const fim = new Date(data);
    fim.setHours(23, 59, 59, 999);
    return fim;
  }

  // --- HELPERS DE TEMPLATE ---

  getNomeTipoAmbiente(tipo: number): string {
    switch (tipo) {
      case 1:
        return 'Produ\u00E7\u00E3o';
      case 2:
        return 'Homologa\u00E7\u00E3o';
      default:
        return 'Desconhecido';
    }
  }

  getNumeroNFCe(nota: NFCeContingenciaStruct): string {
    const num = nota.numero?.toString().padStart(8, '0') || '00000000';
    const serie = nota.serie?.toString().padStart(2, '0') || '00';
    return `${num}/${serie}`;
  }

  getSituacaoLabel(nota: NFCeContingenciaStruct): string {
    switch (nota.statusSecundarioDocumento) {
      case 5:
        return 'Rejeitada';
      case 1:
      case 3:
        return 'Pendente';
      default:
        return 'Pendente';
    }
  }

  getSituacaoClass(nota: NFCeContingenciaStruct): string {
    switch (nota.statusSecundarioDocumento) {
      case 5:
        return 'badge-vermelho';
      default:
        return 'badge-amarelo';
    }
  }

  getRetornoSefaz(nota: NFCeContingenciaStruct): string {
    if (nota.codigoUltimoRetornoSefaz && nota.mensagemUltimoRetornoSefaz) {
      return `${nota.codigoUltimoRetornoSefaz} - ${nota.mensagemUltimoRetornoSefaz}`;
    }
    if (nota.mensagemUltimoRetornoSefaz) {
      return nota.mensagemUltimoRetornoSefaz;
    }
    return '-';
  }

  getClienteDisplay(nota: NFCeContingenciaStruct): string {
    const nome = (nota.nomeDestinatario || '').trim();
    const documento = (nota.cpfCnpjDestinatario || '').trim();

    if (!nome) {
      return 'Cliente avulso';
    }

    if (!documento) {
      return nome;
    }

    return `${documento} | ${nome}`;
  }

  getClienteTooltip(nota: NFCeContingenciaStruct): string {
    return this.getClienteDisplay(nota);
  }

  public onDateKeydown(event: KeyboardEvent): void {
    const permitidas = ['Backspace', 'Delete', 'Tab', 'ArrowLeft', 'ArrowRight', 'Home', 'End', 'Enter'];
    const atalho = (event.ctrlKey || event.metaKey) && ['a', 'c', 'v', 'x'].includes(event.key.toLowerCase());
    if (permitidas.includes(event.key) || atalho) return;
    if (!/^\d$/.test(event.key)) event.preventDefault();
  }
}
